'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import Image from 'next/image';
import { haversineMiles } from '@/shared/geo';
import EventListIcon from '@/assets/icons/event_list.svg';
import { useExplore } from './ExploreProvider';
import ExploreListCard from './ExploreListCard';
import { FilterPanel } from './FilterPanel';
import { HeartsScopeToggle } from './HeartsScopeToggle';
import { SearchInput, FilterButton, LocationButton, SearchBarContainer } from './SearchBar';
import { getActiveCommunityName } from '../hooks/useExploreUrlParams';

/**
 * Sort items based on user location availability
 * - With location: sort by distance (nearest first)
 * - Without location: sort alphanumerically by name
 */
function sortItems(items, userLocation, filters, sortMode) {
  if (!items || items.length === 0) return items;

  // Check if a single community filter is active
  const singleCommunityActive = filters && filters.communities && filters.communities.all === false && filters.communities.selected && filters.communities.selected.length === 1;
  const eventsActive = filters && filters.events === true && filters.hearts === false && filters.artists === false && (!filters.attractions || filters.attractions.all === false && (!filters.attractions.selected || filters.attractions.selected.length === 0)) && (!filters.promotions || filters.promotions.all === false && Object.values(filters.promotions).every(v => v === false)) && filters.communities && filters.communities.all === false && filters.communities.selected.length <= 1;
  if (eventsActive) {
    // Separate event items and non-event items
    const eventItems = items.filter(item => item.type === 'event');
    const otherItems = items.filter(item => item.type !== 'event');
    // Sort only non-event items
    const sortedOtherItems = [...otherItems].sort((a, b) => {
      if (sortMode === 'distance' && userLocation) {
        // Sort by distance when location is available
        const userCoords = [userLocation.lat, userLocation.lng];
        const aCoords = a.location ? [a.location.lat, a.location.lng] : null;
        const bCoords = b.location ? [b.location.lat, b.location.lng] : null;

        // Items without coordinates go to the end
        if (!aCoords && !bCoords) return 0;
        if (!aCoords) return 1;
        if (!bCoords) return -1;
        const aDist = haversineMiles(userCoords, aCoords);
        const bDist = haversineMiles(userCoords, bCoords);
        return aDist - bDist;
      } else {
        // Sort alphanumerically by name when no location
        const aName = (a.name || '').toLowerCase();
        const bName = (b.name || '').toLowerCase();
        return aName.localeCompare(bName, undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      }
    });
    // Return event items in original order, then sorted other items
    return [...eventItems, ...sortedOtherItems];
  }
  // ...existing code...
  return [...items].sort((a, b) => {
    // Only apply order/id sort for attractions when single community filter is active
    if (singleCommunityActive && a.type === 'attraction' && b.type === 'attraction') {
      const aOrder = typeof a.order === 'number' ? a.order : Infinity;
      const bOrder = typeof b.order === 'number' ? b.order : Infinity;
      if (aOrder !== bOrder) return aOrder - bOrder;
      // If order is same, sort by numeric part of id descending
      if (a.id && b.id) {
        const aNum = parseInt(a.id.replace(/[^0-9]/g, ''), 10);
        const bNum = parseInt(b.id.replace(/[^0-9]/g, ''), 10);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return bNum - aNum;
        }
        // fallback to string compare if not numeric
        if (a.id > b.id) return -1;
        if (a.id < b.id) return 1;
      }
    }
    if (sortMode === 'distance' && userLocation) {
      // Sort by distance when location is available
      const userCoords = [userLocation.lat, userLocation.lng];
      const aCoords = a.location ? [a.location.lat, a.location.lng] : null;
      const bCoords = b.location ? [b.location.lat, b.location.lng] : null;

      // Items without coordinates go to the end
      if (!aCoords && !bCoords) return 0;
      if (!aCoords) return 1;
      if (!bCoords) return -1;
      const aDist = haversineMiles(userCoords, aCoords);
      const bDist = haversineMiles(userCoords, bCoords);
      return aDist - bDist;
    } else if (a.type === 'promotion') {
      const aName = new Date(a.endDate || '').setHours(0, 0, 0, 0);
      const bName = new Date(b.endDate || '').setHours(0, 0, 0, 0);
      if (bName - aName !== 0) return aName - bName;
      return a.name.localeCompare(b.name);
    } else {
      // Sort alphanumerically by name when no location
      const aName = (a.name || '').toLowerCase();
      const bName = (b.name || '').toLowerCase();
      return aName.localeCompare(bName, undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    }
  });
}
function ListItem(t0) {
  const $ = _c(18);
  const {
    item,
    userLocation,
    onClick
  } = t0;
  let t1;
  if ($[0] !== item || $[1] !== onClick) {
    t1 = event => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      onClick?.(item);
    };
    $[0] = item;
    $[1] = onClick;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleKeyDown = t1;
  let t2;
  if ($[3] !== item || $[4] !== onClick) {
    t2 = e => {
      e.preventDefault();
      onClick(item);
    };
    $[3] = item;
    $[4] = onClick;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = `${item?.name || item?.title || "View"} details`;
  let t4;
  if ($[6] !== item || $[7] !== onClick) {
    t4 = () => onClick(item);
    $[6] = item;
    $[7] = onClick;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== item || $[10] !== t4 || $[11] !== userLocation) {
    t5 = <ExploreListCard item={item} userLocation={userLocation} onMapClick={t4} />;
    $[9] = item;
    $[10] = t4;
    $[11] = userLocation;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== handleKeyDown || $[14] !== t2 || $[15] !== t3 || $[16] !== t5) {
    t6 = <div onClick={t2} onKeyDown={handleKeyDown} className="cursor-pointer" tabIndex={0} role="button" aria-label={t3}>{t5}</div>;
    $[13] = handleKeyDown;
    $[14] = t2;
    $[15] = t3;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}
function EmptyState() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col items-center justify-center py-12 px-4"><div className="text-gray-400 mb-4"><Image src={EventListIcon} alt="" width={64} height={64} className="w-16 h-16" /></div><h3 className="text-lg font-medium text-gray-900 mb-2">No Results Found</h3><p className="text-sm text-gray-600 text-center max-w-sm">Try searching for hearts, artists, or locations to discover beautiful artwork in your area.</p></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function LoadingList() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="bg-white">{[...Array(8)].map(_temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(_, i) {
  return <div key={i} className=" p-4 animate-pulse"><div className="flex items-center space-x-4"><div className="w-12 h-12 bg-gray-200 rounded-lg" /><div className="flex-1"><div className="flex justify-between items-center mb-2"><div className="h-4 bg-gray-200 rounded w-32" /><div className="h-5 bg-gray-200 rounded w-12" /></div><div className="h-3 bg-gray-200 rounded w-24 mb-2" /><div className="h-3 bg-gray-200 rounded w-40" /></div><div className="w-5 h-5 bg-gray-200 rounded" /></div></div>;
}
function MobileSearchBar(t0) {
  const $ = _c(9);
  const {
    searchInputId
  } = t0;
  let t1;
  if ($[0] !== searchInputId) {
    t1 = <SearchInput id={searchInputId} searchClass="right-27 lg:right-22 pr-2 text-[#D9D9D9]" serchInputClass="pr-10 lg:pr-22 text-[15px] border-r border-[#00ffff] border-0 !shadow-[-2px_2px_20px_0_rgba(0,0,0,0.1)] text-lg text-[#757575] leading-[1em] max-w-[calc(100%-105px)] lg:max-w-full" />;
    $[0] = searchInputId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FilterButton className="!absolute !lg:border-r border-t-0 border-b-0 rounded-[10px] lg:rounded-none px-3 lg:px-2.25 cursor-pointer border-[#D9D9D9] right-13 lg:right-11 bg-white !lg:bg-transparent !shadow-none" />;
    t3 = <LocationButton className="!absolute flex cursor-pointer right-0" />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1) {
    t4 = <div className="relative"><SearchBarContainer>{t1}{t2}{t3}</SearchBarContainer></div>;
    $[4] = t1;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="pt-2.5"><HeartsScopeToggle /></div>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4) {
    t6 = <div className="px-4 lg:pt-4 pt-3 pb-3 sticky top-0 bg-neutral-100 z-10">{t4}{t5}</div>;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
function SectionHeader(t0) {
  const $ = _c(10);
  const {
    title,
    count,
    communityName
  } = t0;
  let t1;
  if ($[0] !== communityName || $[1] !== title) {
    t1 = title && <h2 className="2xl:text-4xl xl:text-3xl text-2xl font-semibold text-gray-900">{title} {communityName && <span>in {communityName}</span>}</h2>;
    $[0] = communityName;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="flex items-center gap-2 flex-wrap">{t1}</div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== count) {
    t3 = count !== undefined && <h2 className="lg:text-2xl text-xl font-semibold leading-[1em] text-[#323232]">{count.toLocaleString()} {count === 1 ? "Result" : "Results"}</h2>;
    $[5] = count;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className="lg:px-0 px-4 lg:py-0 lg:pt-0 pt-1"><div className="flex items-center justify-between"><div>{t2}{t3}</div></div></div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function SortByControl() {
  const $ = _c(11);
  const {
    sortMode,
    requestSortMode
  } = useExplore();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center justify-between pb-1 "><h2 className="text-1xl font-semibold text-[#323232] leading-[1]">Sort By</h2></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== requestSortMode) {
    t1 = event => requestSortMode(event.target.value);
    $[1] = requestSortMode;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <option value="alphabetical">Alphabetical</option>;
    t3 = <option value="distance">Distance</option>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== sortMode || $[6] !== t1) {
    t4 = <select value={sortMode} onChange={t1} className="rounded-[10px] border w-full border-[#E0E0E0] bg-white px-[16px] py-[10.5px] text-[16px] font-[500] text-[#323232] appearance-none cursor-pointer">{t2}{t3}</select>;
    $[5] = sortMode;
    $[6] = t1;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="absolute right-4 top-11 lg:top-12 transform -translate-y-1/2 pointer-events-none"><svg xmlns="http://www.w3.org/2000/svg" width="11" height="6" viewBox="0 0 11 6" fill="none"><path d="M10.5625 1.0625L6.03125 5.59375C5.92708 5.69792 5.82292 5.77083 5.71875 5.8125C5.61458 5.85417 5.5 5.875 5.375 5.875C5.25 5.875 5.13542 5.85417 5.03125 5.8125C4.92708 5.77083 4.82292 5.69792 4.71875 5.59375L0.1875 1.0625C0.125 0.999999 0.0781248 0.932291 0.0468748 0.859374C0.0156248 0.786458 -2.25842e-07 0.708333 -2.29485e-07 0.624999C-2.3677e-07 0.458333 0.0572914 0.312499 0.171875 0.187499C0.286458 0.062499 0.4375 -9.72798e-07 0.625 -9.80994e-07L10.125 -1.39625e-06C10.3125 -1.40445e-06 10.4635 0.0624986 10.5781 0.187499C10.6927 0.312499 10.75 0.458332 10.75 0.624999C10.75 0.666666 10.6875 0.812499 10.5625 1.0625Z" fill="#989898" /></svg></span>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4) {
    t6 = <div className="relative min-w-[10em]">{t0}{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}

/**
 * Get contextual header title based on active filters
 * Note: Community name is shown separately via getActiveCommunityName
 */
function getFilteredTitle(filters) {
  const activeTypes = [];
  const promotionKeys = ['dining', 'attractions', 'sports', 'arts'];
  if (filters.hearts) activeTypes.push('Hearts');
  if (filters.events) activeTypes.push('Events');
  if (filters.artists) activeTypes.push('Artists');
  if (filters.attractions?.all || (filters.attractions?.selected || []).length > 0) {
    activeTypes.push('Attractions');
  }
  const hasActivePromotions = filters.promotions?.all || promotionKeys.some(key => filters.promotions?.[key]);
  if (hasActivePromotions) {
    activeTypes.push('Promotions');
  }

  // If only one type is active, show that type
  if (activeTypes.length === 1) {
    return activeTypes[0];
  }

  // If multiple types, show combined title
  if (activeTypes.length > 1 && activeTypes.length <= 3) {
    return activeTypes.join(' & ');
  }
  return null; // Show default "All Results"
}
export function ListView(t0) {
  const $ = _c(65);
  const {
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const {
    listItems,
    searchResults,
    isLoading,
    searchQuery,
    searchCommitted,
    filters,
    heartsScope,
    userLocation,
    sortMode,
    initialMarkers
  } = useExplore();
  const handleItemClick = _temp2;
  const unsortedItems = searchCommitted && searchQuery ? searchResults : listItems;
  let t2;
  bb0: {
    if (heartsScope === "saved") {
      let t3;
      if ($[0] !== unsortedItems) {
        t3 = unsortedItems.filter(_temp3);
        $[0] = unsortedItems;
        $[1] = t3;
      } else {
        t3 = $[1];
      }
      t2 = t3;
      break bb0;
    }
    if (heartsScope === "visited") {
      let t3;
      if ($[2] !== unsortedItems) {
        t3 = unsortedItems.filter(_temp4);
        $[2] = unsortedItems;
        $[3] = t3;
      } else {
        t3 = $[3];
      }
      t2 = t3;
      break bb0;
    }
    t2 = unsortedItems;
  }
  const scopedItems = t2;
  let t3;
  if ($[4] !== filters || $[5] !== scopedItems || $[6] !== sortMode || $[7] !== userLocation) {
    t3 = sortItems(scopedItems, userLocation, filters, sortMode);
    $[4] = filters;
    $[5] = scopedItems;
    $[6] = sortMode;
    $[7] = userLocation;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const displayItems = t3;
  let t4;
  if ($[9] !== filters || $[10] !== searchCommitted || $[11] !== searchQuery) {
    t4 = searchCommitted && searchQuery ? null : getFilteredTitle(filters);
    $[9] = filters;
    $[10] = searchCommitted;
    $[11] = searchQuery;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const headerTitle = t4;
  console.log("ListView render", {
    displayCount: displayItems.length,
    items: displayItems.map(_temp5)
  });
  let t5;
  if ($[13] !== filters || $[14] !== initialMarkers || $[15] !== searchCommitted || $[16] !== searchQuery) {
    t5 = searchCommitted && searchQuery ? null : getActiveCommunityName(filters, initialMarkers);
    $[13] = filters;
    $[14] = initialMarkers;
    $[15] = searchCommitted;
    $[16] = searchQuery;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const communityName = t5;
  if (isLoading) {
    const t6 = `bg-gray-100 ${className}`;
    let t7;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <FilterPanel className="w-56 flex-shrink-0 h-full" />;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="hidden md:flex h-full">{t7}<div className="flex-1"><LoadingList /></div></div>;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    let t9;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className="md:hidden "><MobileSearchBar searchInputId="searchBarInput" /><LoadingList /></div>;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    let t10;
    if ($[21] !== t6) {
      t10 = <div className={t6}>{t8}{t9}</div>;
      $[21] = t6;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    return t10;
  }
  if (displayItems.length === 0) {
    const t6 = `bg-gray-100 ${className}`;
    let t7;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <FilterPanel className="flex flex-col sticky top-4 h-fit self-start" dynamicHeight={true} />;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    let t8;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="hidden container-wrap lg:flex h-full section-spacing !pb-0"><div className="md:grid grid-cols-[330px_1fr] w-full 2xl:gap-[90px] xl:gap-12 lg:gap-8 gap-5">{t7}<div className="flex-1"><EmptyState /></div></div></div>;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    let t9;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className="lg:hidden "><MobileSearchBar searchInputId="searchBox" /><EmptyState /></div>;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    let t10;
    if ($[26] !== t6) {
      t10 = <div className={t6}>{t8}{t9}</div>;
      $[26] = t6;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    return t10;
  }
  const t6 = ` ${className}`;
  let t7;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <FilterPanel className="sticky top-4 h-fit self-start" dynamicHeight={true} />;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== communityName || $[30] !== displayItems.length || $[31] !== headerTitle) {
    t8 = <SectionHeader title={headerTitle} count={displayItems.length} communityName={communityName} />;
    $[29] = communityName;
    $[30] = displayItems.length;
    $[31] = headerTitle;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SortByControl />;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== t8) {
    t10 = <div className="flex items-end justify-between pb-2">{t8}{t9}</div>;
    $[34] = t8;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== displayItems || $[37] !== userLocation) {
    let t12;
    if ($[39] !== userLocation) {
      t12 = item_2 => <ListItem key={item_2.id} item={item_2} userLocation={userLocation} onClick={handleItemClick} />;
      $[39] = userLocation;
      $[40] = t12;
    } else {
      t12 = $[40];
    }
    t11 = displayItems.map(t12);
    $[36] = displayItems;
    $[37] = userLocation;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[41] !== t11) {
    t12 = <div className="py-4 space-y-3 pb-20">{t11}</div>;
    $[41] = t11;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== t10 || $[44] !== t12) {
    t13 = <div className="hidden container-wrap lg:flex h-full section-spacing !pb-0"><div className="md:grid grid-cols-[330px_1fr] w-full 2xl:gap-[90px] xl:gap-12 lg:gap-8 gap-5">{t7}<div className="flex-1  max-w-[1124px]">{t10}{t12}</div></div></div>;
    $[43] = t10;
    $[44] = t12;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <MobileSearchBar searchInputId="searchFilter" />;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== communityName || $[48] !== displayItems.length || $[49] !== headerTitle) {
    t15 = <SectionHeader title={headerTitle} count={displayItems.length} communityName={communityName} />;
    $[47] = communityName;
    $[48] = displayItems.length;
    $[49] = headerTitle;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== displayItems || $[52] !== userLocation) {
    let t17;
    if ($[54] !== userLocation) {
      t17 = item_3 => <ListItem key={item_3.id} item={item_3} userLocation={userLocation} onClick={handleItemClick} metasClass="md:gap-4!" />;
      $[54] = userLocation;
      $[55] = t17;
    } else {
      t17 = $[55];
    }
    t16 = displayItems.map(t17);
    $[51] = displayItems;
    $[52] = userLocation;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[56] !== t16) {
    t17 = <div className="lg:px-4 py-3 space-y-2">{t16}</div>;
    $[56] = t16;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== t15 || $[59] !== t17) {
    t18 = <div className="lg:hidden pb-20 bg-neutral-100">{t14}{t15}{t17}</div>;
    $[58] = t15;
    $[59] = t17;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  let t19;
  if ($[61] !== t13 || $[62] !== t18 || $[63] !== t6) {
    t19 = <div className={t6}>{t13}{t18}</div>;
    $[61] = t13;
    $[62] = t18;
    $[63] = t6;
    $[64] = t19;
  } else {
    t19 = $[64];
  }
  return t19;
}

// Composable components for reuse
function _temp5(i) {
  return {
    id: i.id,
    name: i.name,
    type: i.type
  };
}
function _temp4(item_1) {
  return item_1.type === "heartVisited";
}
function _temp3(item_0) {
  return item_0.type === "heartSaved";
}
function _temp2(item) {
  if (window) {
    window.location.href = `/hearts/${item.slug}`;
  }
}
export { ListItem, EmptyState, LoadingList, SectionHeader };